import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, BellRing, RotateCcw, Save } from "lucide-react";
import type { FeeReminderSettings, FeeReminderTemplate } from "@costadorada/shared";
import { api } from "@/lib/api";
import { formatCurrency, formatDateOnly } from "@/lib/cn";
import { useAuth } from "@/context/AuthContext";
import { QueryError } from "@/components/QueryError";

interface SettingsResponse {
  settings: FeeReminderSettings;
  defaults: FeeReminderSettings;
  isDefault: boolean;
  updatedAt: string | null;
  lastRunOn: string | null;
  nextRunAt: string | null;
  pendingUnits: number;
  recentRuns: Array<{ date: string; units: number; fees: number }>;
}

type Field = "subject" | "bodyEs" | "bodyEn";
const FIELDS = ["{{fecha}}", "{{apto}}", "{{balance}}", "{{nota}}"] as const;
const DAYS = Array.from({ length: 28 }, (_, i) => i + 1);
const HOURS = Array.from({ length: 15 }, (_, i) => i + 6); // 6:00 a.m. – 8:00 p.m.

/**
 * Finanzas → Recordatorios: when the automatic fee reminders go out (on or off, the days of
 * the month and the hour) and the template every reminder uses, automatic or sent from
 * Cuotas, with a live preview. Everyone in finance sees it; only the administration saves.
 */
export function FeeRemindersPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { user } = useAuth();
  const canEdit = user?.role === "ADMIN";
  const qc = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["fee-reminder-settings"],
    queryFn: async () => (await api.get<SettingsResponse>("/maintenance-fees/reminder-settings")).data,
  });
  const [form, setForm] = useState<FeeReminderSettings | null>(null);
  useEffect(() => {
    if (data && !form) setForm(data.settings);
  }, [data, form]);
  const dirty = !!form && !!data && JSON.stringify(form) !== JSON.stringify(data.settings);

  // The preview follows the template as it is typed, after a short pause.
  const [draft, setDraft] = useState<FeeReminderTemplate | null>(null);
  useEffect(() => {
    if (!form) return;
    const h = setTimeout(() => setDraft({ subject: form.subject, bodyEs: form.bodyEs, bodyEn: form.bodyEn }), 400);
    return () => clearTimeout(h);
  }, [form?.subject, form?.bodyEs, form?.bodyEn]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data: preview, isError: previewError } = useQuery({
    queryKey: ["fee-reminder-template-preview", draft],
    queryFn: async () =>
      (await api.post<{ subject: string; html: string; sample: { unitNumber: string; balance: number } }>("/maintenance-fees/reminder-settings/preview", draft)).data,
    enabled: !!draft && !!draft.subject.trim() && !!draft.bodyEs.trim(),
    placeholderData: (prev) => prev,
  });

  const save = useMutation({
    mutationFn: async () => (await api.put<{ settings: FeeReminderSettings }>("/maintenance-fees/reminder-settings", form)).data,
    onSuccess: (res) => {
      setForm(res.settings);
      qc.invalidateQueries({ queryKey: ["fee-reminder-settings"] });
    },
  });

  // The fields ({{fecha}}…) go in where the cursor was, in the last box used.
  const refs = { subject: useRef<HTMLInputElement>(null), bodyEs: useRef<HTMLTextAreaElement>(null), bodyEn: useRef<HTMLTextAreaElement>(null) };
  const [lastField, setLastField] = useState<Field>("bodyEs");
  const insert = (token: string) => {
    if (!form) return;
    const el = refs[lastField].current;
    const value = form[lastField];
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    setForm({ ...form, [lastField]: value.slice(0, start) + token + value.slice(end) });
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + token.length, start + token.length);
    });
  };
  const set = (k: Field) => (e: { target: { value: string } }) => form && setForm({ ...form, [k]: e.target.value });
  const toggleDay = (d: number) =>
    form && setForm({ ...form, days: form.days.includes(d) ? form.days.filter((x) => x !== d) : [...form.days, d].sort((a, b) => a - b) });
  const hourLabel = (h: number) => new Date(2000, 0, 1, h).toLocaleTimeString(lang === "es" ? "es-PR" : "en-US", { hour: "numeric", minute: "2-digit" });
  const longMoment = (iso: string) =>
    new Intl.DateTimeFormat(lang === "es" ? "es-PR" : "en-US", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: "America/Puerto_Rico",
    }).format(new Date(iso));

  if (isError) return <QueryError />;
  if (isLoading || !data || !form) return <div className="text-slate-500">{t("common.loading")}</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/finance/fees" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-primary">
            <ArrowLeft size={14} /> {t("finance.fees")}
          </Link>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold">
            <BellRing size={22} /> {t("finance.remindersTitle")}
          </h1>
          <p className="text-sm text-slate-500">{t("finance.remindersIntro")}</p>
        </div>
        {canEdit && (
          <button className="btn-primary" disabled={!dirty || save.isPending || form.days.length === 0} onClick={() => save.mutate()}>
            <Save size={16} /> {t("common.save")}
          </button>
        )}
      </div>
      {!canEdit && <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">{t("finance.remindersReadOnly")}</div>}
      {save.isError && <div className="text-sm text-red-600">{(save.error as any)?.response?.data?.message ?? t("common.error")}</div>}
      {save.isSuccess && !dirty && <div className="text-sm text-green-700">{t("finance.remindersSaved")}</div>}
      {dirty && canEdit && <div className="text-sm text-amber-700">{t("finance.remindersUnsaved")}</div>}

      <section className="card space-y-3">
        <label className="flex items-center gap-3">
          <input type="checkbox" className="h-4 w-4" disabled={!canEdit} checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
          <span className="font-medium">{t("finance.remindersEnabled")}</span>
        </label>
        <div className="text-sm text-slate-700">
          {data.nextRunAt ? t("finance.remindersNext", { when: longMoment(data.nextRunAt) }) : t("finance.remindersNextNone")}
        </div>
        <div className="text-sm text-slate-500">{t("finance.remindersPending", { count: data.pendingUnits })}</div>
      </section>

      <section className="card space-y-3">
        <h2 className="font-semibold">{t("finance.remindersSchedule")}</h2>
        <div>
          <div className="label">{t("finance.remindersDays")}</div>
          <div className="grid grid-cols-7 gap-1 sm:max-w-md">
            {DAYS.map((d) => (
              <button
                key={d}
                type="button"
                disabled={!canEdit}
                aria-pressed={form.days.includes(d)}
                onClick={() => toggleDay(d)}
                className={`rounded border px-2 py-1 text-sm ${form.days.includes(d) ? "border-primary bg-primary text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
              >
                {d}
              </button>
            ))}
          </div>
          {form.days.length === 0 && <p className="mt-1 text-xs text-red-600">{t("finance.remindersNoDays")}</p>}
        </div>
        <div>
          <label className="label">{t("finance.remindersHour")}</label>
          <select className="input w-auto" disabled={!canEdit} value={form.hour} onChange={(e) => setForm({ ...form, hour: Number(e.target.value) })}>
            {HOURS.map((h) => (
              <option key={h} value={h}>{hourLabel(h)}</option>
            ))}
          </select>
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <div className="card space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="font-semibold">{t("finance.remindersTemplate")}</h2>
            {canEdit && (
              <button
                type="button"
                className="btn-secondary text-xs"
                onClick={() => setForm({ ...form, subject: data.defaults.subject, bodyEs: data.defaults.bodyEs, bodyEn: data.defaults.bodyEn })}
              >
                <RotateCcw size={12} /> {t("finance.remindersRestore")}
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
            {t("finance.remindersFields")}
            {FIELDS.map((f) => (
              <button key={f} type="button" disabled={!canEdit} onClick={() => insert(f)} className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-slate-700 hover:bg-slate-200">
                {f}
              </button>
            ))}
          </div>
          {/* The field names are passed as values so i18next shows them instead of filling them in. */}
          <p className="text-xs text-slate-500">
            {t("finance.remindersFieldsHint", { fecha: "{{fecha}}", apto: "{{apto}}", balance: "{{balance}}", nota: "{{nota}}", interpolation: { escapeValue: false } })}
          </p>
          <div>
            <label className="label">{t("finance.remindersSubject")}</label>
            <input ref={refs.subject} className="input" readOnly={!canEdit} value={form.subject} onFocus={() => setLastField("subject")} onChange={set("subject")} />
          </div>
          <div>
            <label className="label">{t("finance.remindersBodyEs")}</label>
            <textarea ref={refs.bodyEs} className="input min-h-[260px] font-mono text-xs" readOnly={!canEdit} value={form.bodyEs} onFocus={() => setLastField("bodyEs")} onChange={set("bodyEs")} />
          </div>
          <div>
            <label className="label">{t("finance.remindersBodyEn")}</label>
            <textarea ref={refs.bodyEn} className="input min-h-[220px] font-mono text-xs" readOnly={!canEdit} value={form.bodyEn} onFocus={() => setLastField("bodyEn")} onChange={set("bodyEn")} />
          </div>
        </div>

        <div className="card space-y-2">
          <h2 className="font-semibold">{t("finance.remindersPreview")}</h2>
          {previewError ? (
            <div className="text-sm text-red-600">{t("common.error")}</div>
          ) : !preview ? (
            <div className="text-sm text-slate-500">{t("common.loading")}</div>
          ) : (
            <>
              <p className="text-xs text-slate-500">
                {t("finance.remindersPreviewSample", { unit: preview.sample.unitNumber, balance: formatCurrency(preview.sample.balance) })}
              </p>
              <div className="text-sm">
                <span className="text-slate-500">{t("finance.reminderSubject")}:</span> {preview.subject}
              </div>
              {/* The email as it will arrive; sandboxed, so nothing in it can run. */}
              <iframe
                title={t("finance.remindersPreview")}
                sandbox=""
                srcDoc={`<div style="font-family:system-ui,-apple-system,Segoe UI,sans-serif;font-size:14px;line-height:1.5;color:#1e293b">${preview.html}</div>`}
                className="h-[640px] w-full rounded border border-slate-200 bg-white"
              />
            </>
          )}
        </div>
      </section>

      <section className="card space-y-2">
        <h2 className="font-semibold">{t("finance.remindersRecent")}</h2>
        {data.recentRuns.length === 0 ? (
          <p className="text-sm text-slate-500">{t("finance.remindersRecentNone")}</p>
        ) : (
          <ul className="space-y-1 text-sm text-slate-700">
            {data.recentRuns.map((r) => (
              <li key={r.date}>
                {formatDateOnly(`${r.date}T00:00:00Z`, lang)} · {t("finance.remindersRecentRow", { units: r.units, fees: r.fees })}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
